import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload as UploadIcon, Music, Image as ImageIcon, X, Trash2, Play, Pause, FileAudio, Crown, Mic, BadgeCheck, Search, Sparkles, Pencil } from "lucide-react";
import { api, formatTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePlayer } from "@/contexts/PlayerContext";
import { toast } from "sonner";

const Field = ({ label, children, required, hint }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">
      {label}
      {required && <span className="text-[#FF2E00]"> *</span>}
    </label>
    {children}
    {hint && <p className="text-[11px] text-white/40">{hint}</p>}
  </div>
);

// ---------- Hero ----------
const Hero = () => (
  <div className="relative overflow-hidden rounded-3xl mb-8 p-8 sm:p-10 bg-gradient-to-br from-[#FF2E00]/25 via-[#FF2E00]/15 to-[#E2FF31]/10 border border-white/10">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#E2FF31] font-bold mb-3">
      <FileAudio size={14} /> Artist Studio
    </div>
    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-3">
      Drop your sound.
    </h1>
    <p className="text-base text-white/70 max-w-2xl leading-relaxed">
      Premium artists upload their own MP3s with cover art and metadata — your tracks land in the
      &ldquo;Recently Uploaded&rdquo; feed, your library, and global search instantly.
    </p>
  </div>
);

// ---------- Sign-in gate ----------
const SignInGate = ({ onLogin }) => (
  <div className="text-center py-16 px-4 rounded-2xl bg-white/[0.03] border border-white/8">
    <UploadIcon size={36} className="mx-auto text-white/40 mb-4" />
    <h3 className="text-xl font-semibold mb-2">Sign in to upload</h3>
    <p className="text-white/60 text-sm mb-5 max-w-md mx-auto">
      Uploads are tied to your account so you can manage and delete them.
    </p>
    <button
      data-testid="upload-signin-btn"
      onClick={onLogin}
      className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.03] transition"
    >
      Sign in
    </button>
  </div>
);

// ---------- Premium gate (different copy depending on whether they have Listener-only Premium) ----------
const PremiumGate = ({ hasListenerPremium }) => (
  <div
    data-testid="upload-premium-gate"
    className="relative overflow-hidden text-center py-14 px-6 rounded-3xl bg-gradient-to-br from-[#FF2E00]/15 via-[#FF2E00]/15 to-[#E2FF31]/8 border border-white/10"
  >
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF2E00] to-[#FF2E00] mx-auto mb-4 flex items-center justify-center shadow-lg">
      <Crown size={26} className="text-white" />
    </div>
    <h3 className="font-display text-3xl font-semibold mb-2">
      {hasListenerPremium ? "Upgrade to Artist Premium" : "Uploading is an Artist Premium perk"}
    </h3>
    <p className="text-white/65 text-sm mb-6 max-w-md mx-auto leading-relaxed">
      {hasListenerPremium
        ? "You're already a Listener Premium member — upgrade to Artist Premium ($25/mo, 3 months free) to unlock the Artist Studio and publish your tracks."
        : "Artist Premium ($25/month, 3 months free) unlocks the studio so you can upload your own MP3s, build a public artist profile, and reach the global feed."}
    </p>
    <div className="flex items-center gap-3 justify-center flex-wrap">
      <Link
        to="/premium?plan=artist"
        data-testid="upload-go-premium-btn"
        className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.03] transition flex items-center gap-2"
      >
        <Crown size={16} /> Start 3-month free trial
      </Link>
      <span className="text-xs text-white/40">Then $25/mo · cancel anytime</span>
    </div>
  </div>
);

// ---------- Become an artist (Create new OR Claim existing) ----------
const ArtistOnboarding = ({ onCreated }) => {
  const [tab, setTab] = useState("create"); // 'create' | 'claim'

  return (
    <div
      data-testid="upload-artist-onboarding"
      className="rounded-3xl bg-white/[0.04] border border-white/8 p-6 sm:p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF2E00] to-[#FF2E00] flex items-center justify-center shrink-0">
          <Mic size={22} className="text-white" />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#E2FF31] font-bold mb-0.5">
            One-time setup
          </p>
          <h3 className="font-display text-2xl font-semibold">Set up your artist profile</h3>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 p-1 bg-white/5 rounded-full w-fit">
        <button
          type="button"
          onClick={() => setTab("create")}
          data-testid="onboard-tab-create"
          className={`px-4 py-1.5 text-sm font-semibold rounded-full transition ${
            tab === "create" ? "bg-white text-black" : "text-white/60 hover:text-white"
          }`}
        >
          New artist
        </button>
        <button
          type="button"
          onClick={() => setTab("claim")}
          data-testid="onboard-tab-claim"
          className={`px-4 py-1.5 text-sm font-semibold rounded-full transition flex items-center gap-1.5 ${
            tab === "claim" ? "bg-white text-black" : "text-white/60 hover:text-white"
          }`}
        >
          <BadgeCheck size={14} /> Claim existing
        </button>
      </div>

      {tab === "create" ? (
        <CreateNewArtist onCreated={onCreated} />
      ) : (
        <ClaimExistingArtist onClaimed={onCreated} />
      )}
    </div>
  );
};

const CreateNewArtist = ({ onCreated }) => {
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      toast.error("Display name is required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/uploads/artist-profile", { display_name: displayName.trim(), bio: bio.trim() });
      toast.success(`You're live as ${displayName}!`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create artist profile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <p className="text-sm text-white/60 mb-5 leading-relaxed">
        Pick a stage name + a short bio. This is what listeners see on your tracks. You can edit anytime.
      </p>
      <div className="space-y-4">
        <Field label="Stage / Artist Name" required>
          <input
            data-testid="artist-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your artist alias"
            maxLength={80}
            className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition"
          />
        </Field>
        <Field label="Bio" hint={`${bio.length}/600 — keep it punchy`}>
          <textarea
            data-testid="artist-bio"
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 600))}
            rows={3}
            placeholder="Where you're from, what you make, what to expect…"
            className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition resize-none"
          />
        </Field>
        <button
          type="submit"
          data-testid="artist-create-btn"
          disabled={busy || !displayName.trim()}
          className="bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-6 py-2.5 disabled:opacity-50 transition flex items-center gap-2"
        >
          <Mic size={16} /> {busy ? "Creating…" : "Create artist profile"}
        </button>
      </div>
    </form>
  );
};

const ClaimExistingArtist = ({ onClaimed }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [proofUrl, setProofUrl] = useState("");

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/search", { params: { q: term, entity: "musicArtist", limit: 12 } });
        // Surface artist tiles + collapse duplicates by name+source
        const artists = (r.data.results || []).filter(
          (x) => x.wrapperType === "artist" || x.artistType
        );
        const seen = new Set();
        const out = [];
        for (const a of artists) {
          const key = `${(a.artistName || "").toLowerCase()}::${a._source || "itunes"}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const isDeezer = (a._source === "deezer");
          out.push({
            name: a.artistName,
            id: String(a.artistId || ""),
            source: isDeezer ? "deezer" : "itunes",
            artwork: a.artworkUrl100,
            genre: a.primaryGenreName,
            url: a.artistLinkUrl,
          });
        }
        setResults(out.slice(0, 8));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const claim = async (artist) => {
    setBusy(artist.id);
    try {
      const r = await api.post("/uploads/artist/claim", {
        catalog_source: artist.source.startsWith("deezer") ? "deezer" : artist.source,
        catalog_artist_id: artist.id.replace(/^deezer-artist-/, ""),
        catalog_artist_name: artist.name,
        proof_url: proofUrl.trim() || undefined,
      });
      toast.success(`Claim submitted for ${artist.name} — awaiting verification`);
      onClaimed(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Claim failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <p className="text-sm text-white/60 mb-5 leading-relaxed">
        Already on streaming? Find your existing artist profile (from Apple Music or Deezer) and claim it.
        We&apos;ll review the link in 24–48h and award a <span className="text-[#E2FF31] font-semibold">Verified Artist</span> badge.
      </p>

      <Field label="Search your artist name">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="claim-artist-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Bad Bunny, NewJeans, Morgan Wallen…"
            className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition"
          />
        </div>
      </Field>

      <Field label="Verification link (optional but speeds up review)" hint="Link to your official social media that lists this artist name (Instagram, X, TikTok, official site)">
        <input
          data-testid="claim-proof-url"
          value={proofUrl}
          onChange={(e) => setProofUrl(e.target.value)}
          placeholder="https://instagram.com/your-artist"
          className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition"
        />
      </Field>

      <div className="mt-5">
        {loading ? (
          <p className="text-sm text-white/50">Searching…</p>
        ) : results.length === 0 ? (
          <p className="text-sm text-white/40">
            {q.trim() ? "No matching artists found in the catalog." : "Type your artist name to find your profile."}
          </p>
        ) : (
          <div className="space-y-2" data-testid="claim-results">
            {results.map((a) => (
              <div
                key={`${a.source}-${a.id}-${a.name}`}
                data-testid={`claim-row-${a.id || a.name}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/8 hover:border-white/20 transition"
              >
                {a.artwork ? (
                  <img src={a.artwork} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF2E00]/40 to-[#FF2E00]/40 flex items-center justify-center">
                    <Mic size={18} className="text-white/70" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{a.name}</p>
                  <p className="text-xs text-white/45">
                    {a.genre || "Music"} · <span className="capitalize">{a.source}</span>
                  </p>
                </div>
                <button
                  onClick={() => claim(a)}
                  disabled={busy === a.id || !a.id}
                  data-testid={`claim-btn-${a.id || a.name}`}
                  className="text-xs font-semibold rounded-full px-4 py-2 bg-[#FF2E00] hover:bg-[#E62900] text-white transition flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <BadgeCheck size={13} /> {busy === a.id ? "Claiming…" : "Claim"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Upload form (only for premium artists) ----------
const UploadForm = ({ artistDisplayName, onUploaded }) => {
  const audioRef = useRef(null);
  const coverRef = useRef(null);

  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [trackName, setTrackName] = useState("");
  const [artistName, setArtistName] = useState(artistDisplayName || "");
  const [albumName, setAlbumName] = useState("");
  const [genre, setGenre] = useState("");
  const [durationMs, setDurationMs] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (artistDisplayName) setArtistName(artistDisplayName);
  }, [artistDisplayName]);

  const onAudioPick = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".mp3") && file.type !== "audio/mpeg") {
      toast.error("Only MP3 files are supported");
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast.error("MP3 must be under 30 MB");
      return;
    }
    setAudio(file);
    if (!trackName) setTrackName(file.name.replace(/\.mp3$/i, ""));
    try {
      const url = URL.createObjectURL(file);
      const el = new Audio();
      el.preload = "metadata";
      el.src = url;
      el.onloadedmetadata = () => {
        setDurationMs(Math.round((el.duration || 0) * 1000));
        URL.revokeObjectURL(url);
      };
    } catch { /* noop */ }
  };

  const onCoverPick = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Cover must be JPG, PNG or WEBP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Cover must be under 5 MB");
      return;
    }
    setCover(file);
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!audio || !trackName.trim() || !artistName.trim()) {
      toast.error("Track file, title and artist are required");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("audio", audio);
      if (cover) fd.append("cover", cover);
      fd.append("track_name", trackName.trim());
      fd.append("artist_name", artistName.trim());
      if (albumName.trim()) fd.append("album_name", albumName.trim());
      if (genre.trim()) fd.append("genre", genre.trim());
      if (durationMs) fd.append("duration_ms", String(durationMs));

      const r = await api.post("/uploads/track", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success(`"${r.data.trackName}" uploaded`);
      setAudio(null);
      setCover(null);
      if (coverPreview) URL.revokeObjectURL(coverPreview);
      setCoverPreview(null);
      setTrackName("");
      setAlbumName("");
      setGenre("");
      setDurationMs(0);
      setProgress(0);
      if (audioRef.current) audioRef.current.value = "";
      if (coverRef.current) coverRef.current.value = "";
      onUploaded();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 mb-12 p-6 rounded-3xl bg-white/[0.04] border border-white/8">
      <div>
        <Field label="Cover Art">
          <button
            type="button"
            onClick={() => coverRef.current?.click()}
            data-testid="upload-cover-dropzone"
            className="relative group w-full aspect-square rounded-2xl bg-white/[0.04] border border-dashed border-white/15 hover:border-white/40 hover:bg-white/[0.06] transition overflow-hidden flex items-center justify-center"
          >
            {coverPreview ? (
              <>
                <img src={coverPreview} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center text-xs text-white opacity-0 group-hover:opacity-100">
                  Change
                </span>
              </>
            ) : (
              <div className="flex flex-col items-center gap-2 text-white/50 text-xs">
                <ImageIcon size={28} />
                <span>Add cover</span>
                <span className="text-white/30">JPG/PNG/WEBP · ≤5 MB</span>
              </div>
            )}
          </button>
          <input
            ref={coverRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => onCoverPick(e.target.files?.[0])}
            data-testid="upload-cover-input"
          />
        </Field>
      </div>

      <div className="flex flex-col gap-4">
        <Field label="MP3 File" required>
          <button
            type="button"
            onClick={() => audioRef.current?.click()}
            data-testid="upload-audio-dropzone"
            className="w-full text-left rounded-xl bg-white/[0.04] border border-dashed border-white/15 hover:border-white/40 hover:bg-white/[0.06] px-4 py-3 transition flex items-center gap-3"
          >
            <Music size={22} className="text-white/60 shrink-0" />
            <div className="flex-1 min-w-0">
              {audio ? (
                <>
                  <p className="text-sm font-medium truncate">{audio.name}</p>
                  <p className="text-xs text-white/50">
                    {(audio.size / 1024 / 1024).toFixed(1)} MB
                    {durationMs > 0 ? ` · ${formatTime(durationMs)}` : ""}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">Choose an MP3</p>
                  <p className="text-xs text-white/40">≤30 MB · audio/mpeg</p>
                </>
              )}
            </div>
            {audio && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setAudio(null); setDurationMs(0); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setAudio(null); setDurationMs(0); }}}
                className="p-1.5 hover:bg-white/10 rounded-full"
                data-testid="upload-audio-clear"
              >
                <X size={14} />
              </span>
            )}
          </button>
          <input
            ref={audioRef}
            type="file"
            accept="audio/mpeg,.mp3"
            className="hidden"
            onChange={(e) => onAudioPick(e.target.files?.[0])}
            data-testid="upload-audio-input"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Track Title" required>
            <input
              data-testid="upload-track-name"
              value={trackName}
              onChange={(e) => setTrackName(e.target.value)}
              placeholder="My Awesome Song"
              className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30 transition"
            />
          </Field>
          <Field label="Artist Name" required>
            <input
              data-testid="upload-artist-name"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              placeholder="Your stage name"
              className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30 transition"
            />
          </Field>
          <Field label="Album">
            <input
              data-testid="upload-album-name"
              value={albumName}
              onChange={(e) => setAlbumName(e.target.value)}
              placeholder="(optional)"
              className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30 transition"
            />
          </Field>
          <Field label="Genre">
            <input
              data-testid="upload-genre"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              placeholder="(optional)"
              className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30 transition"
            />
          </Field>
        </div>

        <div className="flex items-center gap-3 mt-2">
          <button
            data-testid="upload-submit-btn"
            type="submit"
            disabled={uploading || !audio || !trackName.trim() || !artistName.trim()}
            className="bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
          >
            <UploadIcon size={16} />
            {uploading ? `Uploading… ${progress}%` : "Upload"}
          </button>
          {uploading && (
            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#FF2E00] transition-all duration-200"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

// ---------- Edit Cover Dialog ----------
const EditCoverDialog = ({ track, onClose, onSaved }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);

  const onPick = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Cover must be JPG, PNG or WEBP");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Cover must be under 5 MB");
      return;
    }
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
  };

  const saveUpload = async () => {
    if (!file) {
      toast.error("Choose an image first");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("cover", file);
      const r = await api.patch(`/uploads/track/${track.trackId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover updated");
      onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const saveAuto = async () => {
    setAutoBusy(true);
    try {
      const fd = new FormData();
      fd.append("auto", "1");
      const r = await api.patch(`/uploads/track/${track.trackId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover auto-fetched");
      onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-fetch failed");
    } finally {
      setAutoBusy(false);
    }
  };

  return (
    <div
      data-testid="edit-cover-dialog"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-3xl bg-zinc-950 border border-white/10 p-6 sm:p-7">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#E2FF31] font-bold mb-1">Edit cover</p>
            <h3 className="font-display text-xl font-semibold truncate max-w-[260px]">{track.trackName}</h3>
            <p className="text-xs text-white/45 truncate">{track.artistName}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="edit-cover-close"
            className="p-2 rounded-full hover:bg-white/10 text-white/60 transition shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="edit-cover-dropzone"
          className="relative group w-full aspect-square rounded-2xl bg-white/[0.04] border border-dashed border-white/15 hover:border-white/40 hover:bg-white/[0.06] transition overflow-hidden flex items-center justify-center mb-4"
        >
          {preview ? (
            <img src={preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : track.artworkUrl100 ? (
            <>
              <img src={track.artworkUrl100} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
              <span className="relative z-10 text-xs text-white/80 bg-black/50 px-3 py-1.5 rounded-full">
                Replace current cover
              </span>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 text-white/50 text-xs">
              <ImageIcon size={32} />
              <span>Pick a new cover</span>
              <span className="text-white/30">JPG/PNG/WEBP · ≤5 MB</span>
            </div>
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
          data-testid="edit-cover-input"
        />

        <div className="flex flex-col gap-2.5">
          <button
            onClick={saveUpload}
            disabled={busy || !file}
            data-testid="edit-cover-save-upload"
            className="bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
          >
            <UploadIcon size={15} /> {busy ? "Saving…" : "Save uploaded cover"}
          </button>
          <button
            onClick={saveAuto}
            disabled={autoBusy}
            data-testid="edit-cover-auto"
            className="bg-white/10 hover:bg-white/15 text-white font-semibold rounded-full px-5 py-2.5 disabled:opacity-50 transition flex items-center justify-center gap-2"
          >
            <Sparkles size={15} /> {autoBusy ? "Searching…" : "Auto-fetch from Deezer / iTunes"}
          </button>
          <p className="text-[11px] text-white/40 text-center mt-1 leading-relaxed">
            Auto-fetch looks up your artist name on Deezer first, then iTunes, then Wikipedia.
          </p>
        </div>
      </div>
    </div>
  );
};

// ---------- My uploads list ----------
const MyUploadsList = ({ items, onDelete, onPlay, onEditCover, current, playing }) => {
  if (items.length === 0) {
    return (
      <p className="text-sm text-white/50 py-8 text-center">
        You haven&apos;t uploaded anything yet. Drop an MP3 above to get started.
      </p>
    );
  }
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/8 overflow-hidden">
      {items.map((t, i) => {
        const isCur = current?.trackId === t.trackId;
        return (
          <div
            key={t.trackId}
            data-testid={`upload-row-${t.trackId}`}
            className="grid grid-cols-[40px_60px_1fr_auto_auto_auto] items-center gap-3 px-4 py-2.5 hover:bg-white/5 border-b border-white/5 last:border-b-0 transition"
          >
            <button
              data-testid={`upload-play-${t.trackId}`}
              onClick={() => onPlay(t, i)}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            >
              {isCur && playing ? (
                <Pause size={14} fill="currentColor" />
              ) : (
                <Play size={14} fill="currentColor" className="ml-0.5" />
              )}
            </button>
            {t.artworkUrl100 ? (
              <img src={t.artworkUrl100} alt="" className="w-10 h-10 rounded object-cover" />
            ) : (
              <div className="w-10 h-10 rounded bg-gradient-to-br from-[#FF2E00]/40 to-[#FF2E00]/40 flex items-center justify-center">
                <Music size={16} />
              </div>
            )}
            <div className="min-w-0">
              <p className={`text-sm font-medium truncate ${isCur ? "text-[#FF2E00]" : "text-white"}`}>
                {t.trackName}
              </p>
              <p className="text-xs text-white/50 truncate">{t.artistName}</p>
            </div>
            <span className="text-xs text-white/40 hidden sm:inline">
              {t.trackTimeMillis ? formatTime(t.trackTimeMillis) : "—"}
            </span>
            <button
              data-testid={`upload-edit-cover-${t.trackId}`}
              onClick={() => onEditCover(t)}
              className="p-2 rounded-full hover:bg-white/10 text-white/50 hover:text-[#E2FF31] transition"
              title="Edit cover art"
            >
              <Pencil size={15} />
            </button>
            <button
              data-testid={`upload-delete-${t.trackId}`}
              onClick={() => onDelete(t)}
              className="p-2 rounded-full hover:bg-white/10 text-white/50 hover:text-[#FF2E00] transition"
              title="Delete"
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ---------- Claim status banner ----------
const ClaimStatusBanner = ({ eligibility }) => {
  if (eligibility?.claim_verified) {
    return (
      <div
        data-testid="claim-verified-banner"
        className="flex items-center gap-3 px-5 py-3 mb-6 rounded-2xl border border-[#E2FF31]/30 bg-[#E2FF31]/8"
      >
        <div className="w-10 h-10 rounded-full bg-[#E2FF31]/15 flex items-center justify-center shrink-0">
          <BadgeCheck size={20} className="text-[#E2FF31]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#E2FF31]">Verified Artist</p>
          <p className="text-xs text-white/55">Your catalog profile has been linked to your 1o1sound account.</p>
        </div>
      </div>
    );
  }
  if (eligibility?.pending_artist_claim) {
    const c = eligibility.pending_artist_claim;
    return (
      <div
        data-testid="claim-pending-banner"
        className="flex items-center gap-3 px-5 py-3 mb-6 rounded-2xl border border-[#FF2E00]/30 bg-[#FF2E00]/8"
      >
        <div className="w-10 h-10 rounded-full bg-[#FF2E00]/15 flex items-center justify-center shrink-0">
          <BadgeCheck size={20} className="text-[#FF2E00] animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Claim pending review</p>
          <p className="text-xs text-white/55 truncate">
            We&apos;re verifying your claim for {c.catalog_artist_name} ({c.catalog_source}).
            Approval usually takes 24–48h.
          </p>
        </div>
      </div>
    );
  }
  return null;
};

// ---------- Page shell ----------
const Upload = () => {
  const { user, login } = useAuth();
  const { current, playing, togglePlay, playTracks } = usePlayer();
  const [eligibility, setEligibility] = useState(null);
  const [myUploads, setMyUploads] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editingCover, setEditingCover] = useState(null);

  const refreshEligibility = async () => {
    try {
      const r = await api.get("/uploads/eligibility");
      setEligibility(r.data);
    } catch {
      setEligibility({ signed_in: !!user, premium: false, has_artist_profile: false });
    }
  };

  const refreshUploads = async () => {
    if (!user) {
      setMyUploads([]);
      return;
    }
    setLoadingList(true);
    try {
      const r = await api.get("/uploads/mine");
      setMyUploads(r.data?.results || []);
    } catch {
      /* ignore — likely 401/402/403 if not eligible */
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    refreshEligibility();
    refreshUploads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onDelete = async (track) => {
    if (!window.confirm(`Delete "${track.trackName}"?`)) return;
    try {
      await api.delete(`/uploads/track/${track.trackId}`);
      toast.success("Deleted");
      setMyUploads((p) => p.filter((t) => t.trackId !== track.trackId));
    } catch {
      toast.error("Delete failed");
    }
  };

  const playRow = (track, i) => {
    if (current?.trackId === track.trackId) togglePlay();
    else playTracks(myUploads, i);
  };

  const onCoverSaved = (updated) => {
    setMyUploads((p) => p.map((t) => (t.trackId === updated.trackId ? updated : t)));
  };

  const renderBody = () => {
    if (!eligibility) return <p className="text-sm text-white/50">Loading…</p>;
    if (!eligibility.signed_in) return <SignInGate onLogin={login} />;
    if (!eligibility.has_artist_tier) {
      const hasListener = eligibility.premium_tier === "premium";
      return <PremiumGate hasListenerPremium={hasListener} />;
    }
    if (!eligibility.has_artist_profile) {
      return <ArtistOnboarding onCreated={refreshEligibility} />;
    }
    return (
      <>
        <ClaimStatusBanner eligibility={eligibility} />
        <UploadForm
          artistDisplayName={eligibility.artist_display_name}
          onUploaded={refreshUploads}
        />
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="font-display text-2xl font-semibold tracking-tight">Your uploads</h2>
          <span className="text-xs text-white/40">
            {myUploads.length} track{myUploads.length === 1 ? "" : "s"}
          </span>
        </div>
        {loadingList ? (
          <p className="text-sm text-white/50">Loading…</p>
        ) : (
          <MyUploadsList
            items={myUploads}
            onDelete={onDelete}
            onPlay={playRow}
            onEditCover={setEditingCover}
            current={current}
            playing={playing}
          />
        )}
      </>
    );
  };

  return (
    <div data-testid="upload-page" className="px-4 sm:px-8 py-6 max-w-5xl mx-auto">
      <Hero />
      {renderBody()}
      {editingCover && (
        <EditCoverDialog
          track={editingCover}
          onClose={() => setEditingCover(null)}
          onSaved={onCoverSaved}
        />
      )}
    </div>
  );
};

export default Upload;
